import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtMoney, fmtDate, getActiveLedgerId } from "@/lib/api";
import { useAssetClasses } from "@/lib/assetClasses";
import { useAuth } from "@/lib/auth";
import ShareQrDialog from "@/components/ShareQrDialog";
import ClassSuggestionBanner from "@/components/ClassSuggestionBanner";
import WelcomeTour from "@/components/WelcomeTour";
import HelpLink from "@/components/HelpLink";
import {
  TrendUp,
  Vault,
  House,
  Wrench,
  Warning,
  ArrowUpRight,
  FilePdf,
  ShareNetwork,
  ShieldCheck,
  Compass,
  Sparkle,
} from "@phosphor-icons/react";

export default function Dashboard() {
  const { user, households } = useAuth();
  const { classes: assetClasses, labelFor, classByKey } = useAssetClasses();
  const categoryLabel = labelFor;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showAllClasses, setShowAllClasses] = useState(() => {
    try { return localStorage.getItem("fl.composition.showAll") === "1"; }
    catch { return false; }
  });

  const toggleShowAll = () => {
    setShowAllClasses((v) => {
      const next = !v;
      try { localStorage.setItem("fl.composition.showAll", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const downloadManifest = async ({ textOnly = false } = {}) => {
    if (exporting) return;
    setExporting(true);
    const label = textOnly ? "text-only manifest" : "Estate Manifest";
    const toastId = toast.loading(
      textOnly
        ? "Generating your text-only manifest… this is the fast path."
        : "Generating your Estate Manifest… this can take up to a minute on larger ledgers.",
    );
    try {
      const ledgerId = getActiveLedgerId();
      const params = {};
      if (ledgerId) params.ledger_id = ledgerId;
      if (textOnly) params.thumbnails = 0;
      const resp = await api.get("/export/manifest-pdf", {
        params,
        responseType: "blob",
        timeout: 90000,
      });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `foundation-ledger-manifest-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Your ${label} is downloading.`, { id: toastId });
    } catch (err) {
      // If the server returned a structured error (504/500), the blob
      // response body is JSON — decode it so the user sees the reason.
      let detail = err?.message || "Something went wrong generating the manifest.";
      const blob = err?.response?.data;
      if (blob && typeof blob.text === "function") {
        try {
          const txt = await blob.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) detail = parsed.detail;
        } catch { /* not JSON — keep default */ }
      }
      const status = err?.response?.status;
      if (!textOnly && (status === 504 || status === 500 || status === 502 || status === 520)) {
        toast.error(
          `${detail} — retrying with a text-only version (no photos).`,
          { id: toastId, duration: 4000 },
        );
        // Kick off the fallback after the current call unwinds.
        setExporting(false);
        setTimeout(() => downloadManifest({ textOnly: true }), 200);
        return;
      }
      toast.error(detail, { id: toastId, duration: 6000 });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api
      .get("/dashboard/summary")
      .then((r) => setSummary(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4" data-testid="dashboard-loading">
        <div className="h-10 bg-[#E0DDD6] w-1/3" />
        <div className="grid grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 bg-white border border-[#E0DDD6]" />
          ))}
        </div>
      </div>
    );
  }

  const s = summary || {};
  const insuranceGap = s.insurance_gap ?? 0;

  return (
    <div data-testid="dashboard" className="space-y-10">
      <WelcomeTour />
      <ClassSuggestionBanner />
      {/* v3.15 — legacy users (onboarded before real household auto-create)
          who somehow ended up with zero households get an inline nudge to
          set one up. Personal ledger is hidden from the switcher, so this
          is now the only path back to a usable state. */}
      {households.length === 0 && (
        <div
          data-testid="no-household-banner"
          className="border border-[#C5A059] bg-[#FFFCF3] px-5 py-4 flex items-start gap-4"
        >
          <House size={20} weight="bold" className="text-[#7A5C3E] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
              One-time setup
            </div>
            <div className="text-sm text-[#0A192F]">
              Create your household ledger to start recording assets, adding rooms, and inviting family or executors.
            </div>
          </div>
          <Link
            to="/households"
            data-testid="no-household-banner-cta"
            className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-2 text-xs font-medium border border-[#0F2818] hover:bg-[#0F2818] shrink-0"
          >
            Create Household <ArrowUpRight size={12} weight="bold" />
          </Link>
        </div>
      )}
      {/* Masthead */}
      <div className="fade-in-up">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3 flex items-center gap-3">
          <span>{user?.household_name ? `${user.household_name} · ` : ""}Volume I · {new Date().getFullYear()} · Standing Record</span>
          <HelpLink anchor="first-ten" label="Your first ten minutes" />
        </div>
        <div className="flex items-end justify-between border-b-2 border-[#C5A059] pb-6 gap-6">
          <h1 className="font-serif-display text-5xl sm:text-6xl text-[#0A192F] leading-[0.95]">
            The Standing<br />Estate Manifest.
          </h1>
          <div className="hidden md:flex flex-col items-end gap-3">
            <div className="text-right">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                As of
              </div>
              <div className="font-mono-ledger text-sm text-[#0A192F]">
                {new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
              </div>
            </div>
            <button
              onClick={() => downloadManifest()}
              disabled={exporting}
              data-testid="export-manifest-btn"
              className="inline-flex items-center gap-2 bg-white text-[#0A192F] px-4 py-2 text-xs font-medium border border-[#0A192F] hover:bg-[#0A192F] hover:text-[#F9F8F6] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <FilePdf size={14} weight="bold" />
              {exporting ? "Generating…" : "Export Estate Manifest"}
            </button>
            <button
              onClick={() => setShareOpen(true)}
              data-testid="share-heirs-btn"
              className="inline-flex items-center gap-2 bg-white text-[#1A3622] px-4 py-2 text-xs font-medium border border-[#1A3622] hover:bg-[#1A3622] hover:text-[#F9F8F6] transition-colors"
            >
              <ShareNetwork size={14} weight="bold" /> Share with Heirs
            </button>
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#E0DDD6] bg-white" data-testid="kpi-row">
        <KpiTile
          label="Total Fair Market"
          value={fmtMoney(s.total_fmv)}
          sub={`${s.total_assets || 0} recorded assets`}
          icon={<TrendUp size={18} weight="regular" />}
          testid="kpi-fmv"
        />
        <KpiTile
          label="Replacement Cost"
          value={fmtMoney(s.total_rcv)}
          sub="New-equivalent retail"
          icon={<Vault size={18} weight="regular" />}
          testid="kpi-rcv"
        />
        <KpiTile
          label="Insurance Gap"
          value={fmtMoney(insuranceGap)}
          sub={insuranceGap > 0 ? "Coverage headroom required" : "Parity"}
          warn={insuranceGap > 0}
          icon={<Warning size={18} weight="regular" />}
          testid="kpi-gap"
        />
        <KpiTile
          label="Home Components"
          value={String(s.home_component_count ?? 0)}
          sub="Tracked specifications"
          icon={<House size={18} weight="regular" />}
          testid="kpi-home"
          last
        />
      </div>

      {/* Transition Guide entry point */}
      <Link
        to="/transition"
        data-testid="dashboard-transition-card"
        className="block bg-white border border-[#E0DDD6] hover:border-[#1A3622] transition-colors"
      >
        <div className="px-6 py-5 flex items-center gap-4">
          <div className="w-11 h-11 bg-[#1A3622] flex items-center justify-center border border-[#0F2818] shrink-0">
            <Compass size={20} weight="regular" color="#F9F8F6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
              Advisory · Assets &amp; Possessions
            </div>
            <div className="font-serif-display text-xl text-[#0A192F] leading-tight mt-0.5">
              Transition Guide.
            </div>
            <div className="text-xs text-[#5C5F58] mt-1">
              Routine tidying, smart-sizing, downsizing, or formal dispensation — Leo composes
              guidance across six dimensions grounded in the knowledge base.
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-[#1A3622] shrink-0">
            <Sparkle size={12} weight="bold" /> Begin
          </div>
        </div>
      </Link>

      {/* Split: Category breakdown + Upcoming maintenance */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Category ledger */}
        <section className="lg:col-span-3 border border-[#E0DDD6] bg-white" data-testid="category-ledger">
          <header className="px-6 py-4 border-b border-[#E0DDD6] flex items-center justify-between">
            <div>
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                Section I
              </div>
              <h2 className="font-serif-display text-2xl text-[#0A192F]">Composition by Class</h2>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={toggleShowAll}
                data-testid="composition-toggle-all"
                className="text-xs text-[#5C5F58] hover:text-[#1A3622] underline underline-offset-2"
                title={showAllClasses ? "Hide classes with no assets" : "Show every class in your taxonomy"}
              >
                {showAllClasses ? "Hide empty classes" : "Show all classes"}
              </button>
              <Link to="/assets" className="text-sm text-[#1A3622] underline underline-offset-4 hover:text-[#0F2818]" data-testid="category-see-all">
                Open Ledger →
              </Link>
            </div>
          </header>
          <div className="divide-y divide-[#E0DDD6]" data-testid="composition-rows">
            {(() => {
              const counts = s.by_category || {};
              // Union of: every class in this ledger's taxonomy + any orphan keys
              // present in counts that aren't in the taxonomy (legacy / deleted classes).
              const keys = new Set(assetClasses.map((c) => c.key));
              Object.keys(counts).forEach((k) => keys.add(k));
              const allRows = Array.from(keys)
                .map((key) => ({ key, count: counts[key] || 0 }))
                .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
              const rows = showAllClasses ? allRows : allRows.filter((r) => r.count > 0);
              const hiddenCount = allRows.length - rows.length;
              const total = s.total_assets || 1;
              if (rows.length === 0) {
                return (
                  <div className="px-6 py-8 text-center text-sm text-[#5C5F58]" data-testid="composition-empty">
                    No assets recorded yet.
                  </div>
                );
              }
              return (
                <>
                  {rows.map(({ key, count }) => {
                    const pct = Math.round((count / total) * 100);
                    const cls = classByKey(key);
                    const isCustom = cls && cls.is_system === false;
                    return (
                      <div
                        key={key}
                        data-testid={`composition-row-${key}`}
                        data-is-custom={isCustom ? "true" : "false"}
                        className="px-6 py-4 grid grid-cols-[1fr_auto_auto] items-center gap-6"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-2 h-2 shrink-0"
                              style={{ backgroundColor: cls?.color || "#5C5F58" }}
                              data-testid={`composition-swatch-${key}`}
                              aria-hidden="true"
                            />
                            <div className="text-sm text-[#0A192F] font-medium">{categoryLabel(key)}</div>
                            {isCustom && (
                              <span
                                data-testid={`composition-custom-pill-${key}`}
                                className="inline-block px-1.5 py-0.5 text-[9px] font-mono-ledger uppercase tracking-[0.15em] text-[#5C3D08] bg-[#F4E9D2] border border-[#C5A059]"
                                title="User-defined asset class"
                              >
                                Custom
                              </span>
                            )}
                          </div>
                          <div className="mt-2 h-[2px] bg-[#F9F8F6]">
                            <div className="h-full" style={{ width: `${count ? pct : 0}%`, backgroundColor: cls?.color || (isCustom ? "#C5A059" : "#1A3622") }} />
                          </div>
                        </div>
                        <div className="font-mono-ledger text-sm text-[#0A192F] tabular-nums">
                          {count}
                        </div>
                        <div className="font-mono-ledger text-xs text-[#5C5F58] w-10 text-right tabular-nums">
                          {count ? `${pct}%` : "—"}
                        </div>
                      </div>
                    );
                  })}
                  {!showAllClasses && hiddenCount > 0 && (
                    <button
                      onClick={toggleShowAll}
                      data-testid="composition-hidden-notice"
                      className="w-full px-6 py-2 text-left font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] hover:text-[#1A3622] hover:bg-[#F9F8F6]"
                    >
                      + {hiddenCount} empty class{hiddenCount === 1 ? "" : "es"} hidden
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </section>

        {/* Upcoming maintenance */}
        <section className="lg:col-span-2 border border-[#E0DDD6] bg-white" data-testid="upcoming-maintenance">
          <header className="px-6 py-4 border-b border-[#E0DDD6] flex items-center justify-between">
            <div>
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                Section II
              </div>
              <h2 className="font-serif-display text-2xl text-[#0A192F]">Pending Actions</h2>
            </div>
            <Wrench size={18} color="#5C5F58" />
          </header>
          {(!s.upcoming_maintenance || s.upcoming_maintenance.length === 0)
            && (!s.warranty_actions || s.warranty_actions.length === 0) ? (
            <div className="px-6 py-10 text-center">
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                Log clear
              </div>
              <p className="text-sm text-[#5C5F58]">No pending warranties or maintenance.</p>
              <Link to="/maintenance" className="mt-4 inline-block text-sm text-[#1A3622] underline underline-offset-4" data-testid="maintenance-empty-cta">
                Add a task →
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[#E0DDD6]" data-testid="pending-actions-list">
              {(s.warranty_actions || []).map((w) => (
                <WarrantyActionRow key={`w-${w.asset_id}`} action={w} />
              ))}
              {s.upcoming_maintenance.map((t) => (
                <li key={t.id} className="px-6 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm text-[#0A192F]">{t.title}</div>
                    <div className="font-mono-ledger text-xs text-[#5C5F58] mt-1">{fmtDate(t.due_date)}</div>
                  </div>
                  <ArrowUpRight size={14} color="#5C5F58" />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Recent entries */}
      <section className="border border-[#E0DDD6] bg-white" data-testid="recent-entries">
        <header className="px-6 py-4 border-b border-[#E0DDD6] flex items-center justify-between">
          <div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
              Section III
            </div>
            <h2 className="font-serif-display text-2xl text-[#0A192F]">Most Recent Entries</h2>
          </div>
          <Link to="/assets" className="text-sm text-[#1A3622] underline underline-offset-4" data-testid="recent-view-all">
            View all →
          </Link>
        </header>
        {(!s.recent_assets || s.recent_assets.length === 0) ? (
          <div className="px-6 py-16 text-center">
            <p className="font-serif-display text-2xl text-[#0A192F] mb-2">No entries yet.</p>
            <p className="text-sm text-[#5C5F58] mb-6 max-w-md mx-auto">
              The ledger is empty. Begin your manifest by recording your first asset — narrate, photograph, or import an inventory document.
            </p>
            <Link
              to="/assets/new"
              data-testid="empty-state-add-btn"
              className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818]"
            >
              Record the First Asset →
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F9F8F6] border-b border-[#E0DDD6]">
              <tr>
                <Th>#</Th>
                <Th>Name</Th>
                <Th>Class</Th>
                <Th>Location</Th>
                <Th className="text-right">FMV</Th>
                <Th>Recorded</Th>
              </tr>
            </thead>
            <tbody>
              {s.recent_assets.map((a, i) => (
                <tr key={a.id} className="border-b border-[#E0DDD6] hover:bg-[#F9F8F6]/60">
                  <td className="px-4 py-3 font-mono-ledger text-xs text-[#5C5F58] tabular-nums">
                    {String(i + 1).padStart(3, "0")}
                  </td>
                  <td className="px-4 py-3 text-[#0A192F]">
                    <Link to={`/assets/${a.id}`} className="hover:underline underline-offset-4" data-testid={`recent-asset-${a.id}`}>
                      {a.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-[#5C5F58]">{categoryLabel(a.category)}</td>
                  <td className="px-4 py-3 text-[#5C5F58]">{a.location || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-ledger text-[#0A192F] tabular-nums">
                    {fmtMoney(a.fmv)}
                  </td>
                  <td className="px-4 py-3 font-mono-ledger text-xs text-[#5C5F58]">{fmtDate(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <ShareQrDialog open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-left font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58] ${className}`}>
      {children}
    </th>
  );
}

function KpiTile({ label, value, sub, icon, warn, testid, last }) {
  return (
    <div
      data-testid={testid}
      className={`px-6 py-6 border-[#E0DDD6] ${last ? "" : "border-b sm:border-b-0 sm:border-r lg:border-b-0"}`}
    >
      <div className="flex items-center justify-between">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
          {label}
        </div>
        <div className={warn ? "text-[#6B1718]" : "text-[#5C5F58]"}>{icon}</div>
      </div>
      <div className={`font-serif-display text-4xl mt-3 tabular-nums ${warn ? "text-[#6B1718]" : "text-[#0A192F]"}`}>
        {value}
      </div>
      <div className="font-mono-ledger text-xs text-[#5C5F58] mt-2">{sub}</div>
    </div>
  );
}


function WarrantyActionRow({ action }) {
  const isUrgent = action.tier === "expiring_urgent";
  const isSoon = action.tier === "expiring_soon";
  const isUnreg = action.tier === "unregistered";
  const pill = isUrgent
    ? "bg-[#6B1718] text-[#F9F8F6]"
    : isSoon
      ? "bg-[#9C5012] text-[#F9F8F6]"
      : "bg-[#C5A059] text-[#0A192F]";
  const label = isUrgent
    ? "EXPIRES SOON"
    : isSoon
      ? "EXPIRES SOON"
      : "REGISTER";
  const summary = isUnreg
    ? `Register ${action.brand || "this asset"}`
    : `${action.brand || "Warranty"} expires in ${action.days_until_expiry}d`;
  return (
    <li
      data-testid={`pending-warranty-${action.asset_id}`}
      data-warranty-tier={action.tier}
      className="px-6 py-3 flex items-center justify-between"
    >
      <Link to={`/assets/${action.asset_id}`} className="flex-1 min-w-0 group">
        <div className="flex items-center gap-2">
          <span className={`px-1.5 py-0.5 text-[9px] font-mono-ledger uppercase tracking-[0.15em] shrink-0 ${pill}`}>
            {label}
          </span>
          <span className="text-sm text-[#0A192F] truncate group-hover:underline underline-offset-4">
            {summary} · {action.asset_name}
          </span>
        </div>
        <div className="font-mono-ledger text-xs text-[#5C5F58] mt-1">
          {isUnreg
            ? "Manufacturer registration recommended"
            : `Coverage ends ${action.warranty_expiration_date}`}
        </div>
      </Link>
      <ShieldCheck size={14} color={isUrgent ? "#6B1718" : isSoon ? "#9C5012" : "#5C5F58"} />
    </li>
  );
}
